/**
 * Proposal Review Page
 *
 * Review regulatory coverage gaps and approve/reject config proposals.
 * Architect-only page for gap analysis and config proposal lifecycle.
 *
 * Proposals are displayed as structured human-readable content.
 * The backend config storage format is an implementation detail that
 * NEVER reaches the frontend — all fields are parsed server-side.
 *
 * DRR-172: Regulation-first config surfacing pipeline.
 */

import { useMemo, useState } from 'react';
import { useDisclosure } from '@mantine/hooks';
import { useQuery, useMutation } from '@apollo/client/react';

import {
  useGetPendingProposalsQuery,
  useGetCoverageGapsQuery,
  useListRulesetsQuery,
  useGenerateProposalsMutation,
  useApproveProposalMutation,
  useExplainProposalMutation,
  ListRulesetsDocument,
  GetPendingProposalsDocument,
} from '@/generated/graphql';
import {
  IconAlertTriangle,
  IconSearch,
  IconShieldCheck,
  IconShieldX,
  IconFileAnalytics,
  IconCheck,
  IconX,
  IconSparkles,
  IconListCheck,
  IconFile,
  IconArrowRight,
  IconScale,
  IconLink,
  IconEye,
} from '@tabler/icons-react';
import {
  Container,
  Title,
  Text,
  Card,
  Group,
  Stack,
  Badge,
  ThemeIcon,
  Alert,
  SimpleGrid,
  Loader,
  Table,
  Select,
  Button,
  Code,
  Tabs,
  Modal,
  Divider,
  List,
  Timeline,
} from '@mantine/core';
// ============================================================================
// GraphQL Queries & Mutations
// ============================================================================

// ============================================================================
// Types
// ============================================================================

interface ExplainResult {
  explainConfigProposal: {
    response: string;
  };
}

interface ProposalEdge {
  edgeType: string;
  target: string;
  strength: string | null;
}

interface ProposalProvision {
  section: string;
  text: string;
  significance: string;
  requiredConfigTypes: string[];
}

interface ProposalWorkflowState {
  name: string;
  stateType: string;
  displayName: string | null;
  policyRefs: string[];
}

interface ProposalWorkflowTransition {
  fromState: string;
  toState: string;
  event: string;
}

interface PendingProposal {
  proposalId: string;
  configType: string;
  displayName: string | null;
  description: string | null;
  safetyLevel: string | null;
  status: string;
  sourceRule: string | null;
  generatedAt: string | null;
  statuteRef: string | null;
  trigger: string | null;
  setting: string | null;
  subgraph: string | null;
  edges: ProposalEdge[];
  provisions: ProposalProvision[];
  states: ProposalWorkflowState[];
  transitions: ProposalWorkflowTransition[];
}

interface PendingProposalsData {
  pendingProposals: {
    totalCount: number;
    ruleCount: number;
    taskGroupCount: number;
    workflowCount: number;
    proposals: PendingProposal[];
  };
}

interface CoverageGap {
  ruleId: string;
  ruleName: string;
  missingConfigs: {
    configType: string;
    description: string;
    expectedTrigger: string;
  }[];
}

interface CoverageGapsData {
  regulatoryCoverageGaps: {
    rulesetId: string;
    totalRules: number;
    coveredRules: number;
    coveragePercent: number;
    gaps: CoverageGap[];
  };
}

interface GenerateProposalsResult {
  generateConfigProposals: {
    success: boolean;
    draftCount: number;
    proposals: PendingProposal[];
    gapIds: string[];
    warnings: string[];
  };
}

interface ApproveProposalResult {
  approveConfigProposal: {
    success: boolean;
    proposalId: string;
    movedTo: string;
    auditEventId: string;
  };
}

// ============================================================================
// Constants
// ============================================================================

const CONFIG_TYPE_COLORS: Record<string, string> = {
  rule: 'blue',
  task_group: 'teal',
  workflow: 'violet',
};

const CONFIG_TYPE_LABELS: Record<string, string> = {
  rule: 'Regulatory Rule',
  task_group: 'Task Group',
  workflow: 'Workflow',
};

const SAFETY_COLORS: Record<string, string> = {
  critical: 'red',
  high: 'orange',
  medium: 'yellow',
  low: 'green',
  advisory: 'gray',
};

const EDGE_TYPE_LABELS: Record<string, string> = {
  MANDATED_BY: 'Mandated By',
  IMPROVES: 'Improves',
  MITIGATES: 'Mitigates',
  REIMBURSES_VIA: 'Reimburses Via',
  REQUIRES_COMPETENCY: 'Requires Competency',
  EVIDENCED_BY: 'Evidenced By',
  FULFILLS: 'Fulfills',
  DEPENDS_ON: 'Depends On',
};

const STATE_TYPE_COLORS: Record<string, string> = {
  initial: 'green',
  operational: 'blue',
  terminal: 'gray',
};

// ============================================================================
// Proposal Detail Modal
// ============================================================================

function buildProposalSummary(p: PendingProposal): string {
  const parts: string[] = [];
  parts.push(`Type: ${CONFIG_TYPE_LABELS[p.configType] ?? p.configType}`);
  if (p.displayName) parts.push(`Name: ${p.displayName}`);
  if (p.description) parts.push(`Description: ${p.description}`);
  if (p.safetyLevel) parts.push(`Safety Level: ${p.safetyLevel}`);
  if (p.statuteRef) parts.push(`Statute Reference: ${p.statuteRef}`);
  if (p.trigger) parts.push(`Trigger Event: ${p.trigger}`);
  if (p.setting) parts.push(`Setting: ${p.setting}`);
  if (p.edges.length > 0) {
    parts.push(`Relationships: ${p.edges.map((e) => `${EDGE_TYPE_LABELS[e.edgeType] ?? e.edgeType} → ${e.target}`).join('; ')}`);
  }
  if (p.provisions.length > 0) {
    parts.push(`Provisions: ${p.provisions.map((pv) => `${pv.section}: ${pv.significance}`).join('; ')}`);
  }
  if (p.states.length > 0) {
    parts.push(`Workflow States: ${p.states.map((s) => s.displayName ?? s.name).join(' → ')}`);
  }
  return parts.join('\n');
}

function ProposalDetailModal({
  proposal,
  opened,
  onClose,
  onApprove,
  onReject,
  approving,
}: {
  proposal: PendingProposal | null;
  opened: boolean;
  onClose: () => void;
  onApprove: (id: string) => void;
  onReject: (id: string) => void;
  approving: boolean;
}) {
  const [explanation, setExplanation] = useState<string | null>(null);
  const [explainProposal, { loading: explaining }] = useExplainProposalMutation();

  const handleExplain = async () => {
    if (!proposal) return;
    const result = await explainProposal({
      variables: {
        input: { proposalSummary: buildProposalSummary(proposal) },
      },
    });
    if (result.data?.explainConfigProposal.response) {
      setExplanation(result.data.explainConfigProposal.response);
    }
  };

  const handleClose = () => {
    setExplanation(null);
    onClose();
  };

  if (!proposal) return null;

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        <Group gap="xs">
          <Code fw={700}>{proposal.proposalId}</Code>
          <Badge variant="light" size="sm" color={CONFIG_TYPE_COLORS[proposal.configType] ?? 'gray'}>
            {CONFIG_TYPE_LABELS[proposal.configType] ?? proposal.configType}
          </Badge>
          {proposal.safetyLevel && (
            <Badge variant="light" size="sm" color={SAFETY_COLORS[proposal.safetyLevel] ?? 'gray'}>
              {proposal.safetyLevel}
            </Badge>
          )}
        </Group>
      }
      size="lg"
      scrollAreaComponent={undefined}
    >
      <Stack gap="md">
        {/* Header info */}
        {proposal.displayName && (
          <Title order={4}>{proposal.displayName}</Title>
        )}
        {proposal.description && (
          <Text size="sm" c="dimmed">{proposal.description}</Text>
        )}

        {/* Metadata */}
        <SimpleGrid cols={2} spacing="xs">
          {proposal.sourceRule && (
            <Group gap="xs">
              <Text size="xs" c="dimmed">Source Rule:</Text>
              <Code fz="xs">{proposal.sourceRule}</Code>
            </Group>
          )}
          {proposal.statuteRef && (
            <Group gap="xs">
              <Text size="xs" c="dimmed">Statute:</Text>
              <Text size="xs" fw={500}>{proposal.statuteRef}</Text>
            </Group>
          )}
          {proposal.trigger && (
            <Group gap="xs">
              <Text size="xs" c="dimmed">Trigger:</Text>
              <Badge size="xs" variant="outline">{proposal.trigger}</Badge>
            </Group>
          )}
          {proposal.setting && (
            <Group gap="xs">
              <Text size="xs" c="dimmed">Setting:</Text>
              <Text size="xs">{proposal.setting}</Text>
            </Group>
          )}
        </SimpleGrid>

        {/* Relationships (Edges) */}
        {proposal.edges.length > 0 && (
          <>
            <Divider label="Relationships" labelPosition="left" />
            <Table fz="sm">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Type</Table.Th>
                  <Table.Th>Target</Table.Th>
                  <Table.Th>Strength</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {proposal.edges.map((edge, i) => (
                  <Table.Tr key={i}>
                    <Table.Td>
                      <Group gap={4}>
                        <IconLink size={12} />
                        <Text size="xs">{EDGE_TYPE_LABELS[edge.edgeType] ?? edge.edgeType}</Text>
                      </Group>
                    </Table.Td>
                    <Table.Td><Code fz="xs">{edge.target}</Code></Table.Td>
                    <Table.Td><Text size="xs">{edge.strength ?? 'primary'}</Text></Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </>
        )}

        {/* Regulatory Provisions (Rule proposals) */}
        {proposal.provisions.length > 0 && (
          <>
            <Divider label="Regulatory Provisions" labelPosition="left" />
            <Stack gap="sm">
              {proposal.provisions.map((prov, i) => (
                <Card key={i} withBorder p="xs" bg="var(--mantine-color-dark-7)">
                  <Group gap="xs" mb={4}>
                    <IconScale size={14} />
                    <Text size="xs" fw={600}>{prov.section}</Text>
                  </Group>
                  <Text size="xs" mb={4}>{prov.text}</Text>
                  <Text size="xs" c="dimmed" fs="italic">{prov.significance}</Text>
                  {prov.requiredConfigTypes.length > 0 && (
                    <Group gap={4} mt={4}>
                      <Text size="xs" c="dimmed">Requires:</Text>
                      {prov.requiredConfigTypes.map((ct) => (
                        <Badge key={ct} size="xs" variant="outline">{ct}</Badge>
                      ))}
                    </Group>
                  )}
                </Card>
              ))}
            </Stack>
          </>
        )}

        {/* Workflow States & Transitions */}
        {proposal.states.length > 0 && (
          <>
            <Divider label="Workflow Flow" labelPosition="left" />
            <Timeline active={proposal.states.length - 1} bulletSize={20} lineWidth={2}>
              {proposal.states.map((state) => {
                const outgoing = proposal.transitions.filter((t) => t.fromState === state.name);
                return (
                  <Timeline.Item
                    key={state.name}
                    bullet={
                      <ThemeIcon
                        size={20}
                        radius="xl"
                        color={STATE_TYPE_COLORS[state.stateType] ?? 'blue'}
                        variant="filled"
                      >
                        <Text size="xs" fw={700}>
                          {state.stateType === 'initial' ? 'S' : state.stateType === 'terminal' ? 'E' : 'O'}
                        </Text>
                      </ThemeIcon>
                    }
                    title={
                      <Group gap="xs">
                        <Text size="sm" fw={600}>{state.displayName ?? state.name}</Text>
                        <Badge size="xs" variant="light" color={STATE_TYPE_COLORS[state.stateType] ?? 'blue'}>
                          {state.stateType}
                        </Badge>
                      </Group>
                    }
                  >
                    {state.policyRefs.length > 0 && (
                      <Group gap={4} mt={2}>
                        <Text size="xs" c="dimmed">Policies:</Text>
                        {state.policyRefs.map((ref) => (
                          <Code key={ref} fz="xs">{ref}</Code>
                        ))}
                      </Group>
                    )}
                    {outgoing.length > 0 && (
                      <List size="xs" mt={4} icon={<IconArrowRight size={10} />}>
                        {outgoing.map((t, ti) => (
                          <List.Item key={ti}>
                            <Text size="xs" span>
                              on <Code fz="xs">{t.event}</Code> {' \u2192 '} <Text span fw={500} size="xs">{t.toState}</Text>
                            </Text>
                          </List.Item>
                        ))}
                      </List>
                    )}
                  </Timeline.Item>
                );
              })}
            </Timeline>
          </>
        )}

        {/* AI Explanation */}
        {explanation && (
          <>
            <Divider label="AI Explanation" labelPosition="left" />
            <Card withBorder p="sm" bg="var(--mantine-color-dark-7)">
              <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{explanation}</Text>
            </Card>
          </>
        )}

        {/* Actions */}
        <Divider />
        <Group justify="space-between">
          <Button
            size="sm"
            variant="light"
            leftSection={<IconSparkles size={14} />}
            onClick={handleExplain}
            loading={explaining}
            disabled={!!explanation}
          >
            {explanation ? 'Explained' : 'Explain This'}
          </Button>
          <Group gap="xs">
            <Button
              size="sm"
              color="red"
              variant="light"
              leftSection={<IconX size={14} />}
              onClick={() => { onReject(proposal.proposalId); handleClose(); }}
            >
              Reject
            </Button>
            <Button
              size="sm"
              color="green"
              leftSection={<IconCheck size={14} />}
              onClick={() => { onApprove(proposal.proposalId); handleClose(); }}
              loading={approving}
            >
              Approve
            </Button>
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
}

// ============================================================================
// Stats Cards
// ============================================================================

function ProposalStatsCards({ data }: { data: PendingProposalsData['pendingProposals'] }) {
  return (
    <SimpleGrid cols={{ base: 2, sm: 4 }} mb="lg">
      <Card withBorder p="sm">
        <Group gap="xs">
          <ThemeIcon size="sm" variant="light" color="blue"><IconFile size={14} /></ThemeIcon>
          <Text size="xs" c="dimmed">Total Proposals</Text>
        </Group>
        <Text size="lg" fw={700}>{data.totalCount}</Text>
      </Card>
      <Card withBorder p="sm">
        <Group gap="xs">
          <ThemeIcon size="sm" variant="light" color="blue"><IconListCheck size={14} /></ThemeIcon>
          <Text size="xs" c="dimmed">Rules</Text>
        </Group>
        <Text size="lg" fw={700}>{data.ruleCount}</Text>
      </Card>
      <Card withBorder p="sm">
        <Group gap="xs">
          <ThemeIcon size="sm" variant="light" color="teal"><IconListCheck size={14} /></ThemeIcon>
          <Text size="xs" c="dimmed">Task Groups</Text>
        </Group>
        <Text size="lg" fw={700}>{data.taskGroupCount}</Text>
      </Card>
      <Card withBorder p="sm">
        <Group gap="xs">
          <ThemeIcon size="sm" variant="light" color="violet"><IconListCheck size={14} /></ThemeIcon>
          <Text size="xs" c="dimmed">Workflows</Text>
        </Group>
        <Text size="lg" fw={700}>{data.workflowCount}</Text>
      </Card>
    </SimpleGrid>
  );
}

function CoverageStatsCards({ data }: { data: CoverageGapsData['regulatoryCoverageGaps'] }) {
  const coverageColor = data.coveragePercent >= 80 ? 'green' : data.coveragePercent >= 50 ? 'yellow' : 'red';

  return (
    <SimpleGrid cols={{ base: 2, sm: 4 }} mb="lg">
      <Card withBorder p="sm">
        <Group gap="xs">
          <ThemeIcon size="sm" variant="light" color="blue"><IconListCheck size={14} /></ThemeIcon>
          <Text size="xs" c="dimmed">Total Rules</Text>
        </Group>
        <Text size="lg" fw={700}>{data.totalRules}</Text>
      </Card>
      <Card withBorder p="sm">
        <Group gap="xs">
          <ThemeIcon size="sm" variant="light" color="green"><IconShieldCheck size={14} /></ThemeIcon>
          <Text size="xs" c="dimmed">Covered</Text>
        </Group>
        <Text size="lg" fw={700}>{data.coveredRules}</Text>
      </Card>
      <Card withBorder p="sm">
        <Group gap="xs">
          <ThemeIcon size="sm" variant="light" color={coverageColor}><IconFileAnalytics size={14} /></ThemeIcon>
          <Text size="xs" c="dimmed">Coverage %</Text>
        </Group>
        <Text size="lg" fw={700}>{data.coveragePercent.toFixed(1)}%</Text>
      </Card>
      <Card withBorder p="sm">
        <Group gap="xs">
          <ThemeIcon size="sm" variant="light" color="red"><IconShieldX size={14} /></ThemeIcon>
          <Text size="xs" c="dimmed">Gaps</Text>
        </Group>
        <Text size="lg" fw={700}>{data.gaps.length}</Text>
      </Card>
    </SimpleGrid>
  );
}

// ============================================================================
// Main Page
// ============================================================================

export function ProposalReviewPage() {
  const [rulesetId, setRulesetId] = useState<string | null>(null);
  const [actionedIds, setActionedIds] = useState<Record<string, 'approved' | 'rejected'>>({});
  const [selectedProposal, setSelectedProposal] = useState<PendingProposal | null>(null);
  const [modalOpened, { open: openModal, close: closeModal }] = useDisclosure(false);

  // Load available rulesets for the dropdown
  const { data: rulesetsData } = useQuery<{ ruleSets: { rulesetId: string; name: string }[] }>(ListRulesetsDocument);

  const rulesetOptions = useMemo(() => {
    if (!rulesetsData?.ruleSets) return [];
    return rulesetsData.ruleSets
      .map((rs) => ({
        value: rs.rulesetId,
        label: `${rs.rulesetId} — ${rs.name.replace(/_/g, ' ')}`,
      }))
      .sort((a, b) => a.value.localeCompare(b.value));
  }, [rulesetsData]);

  // Load pending proposals on mount
  const {
    data: proposalsData,
    loading: proposalsLoading,
    error: proposalsError,
    refetch: refetchProposals,
  } = useQuery<PendingProposalsData>(GetPendingProposalsDocument);

  // Coverage gap search (lazy -- only after ruleset selected)
  const { data: gapsData, loading: gapsLoading, error: gapsError } = useGetCoverageGapsQuery({
      variables: { rulesetId: rulesetId ?? '' },
      skip: !rulesetId,
    },
  );

  const [generateProposals, { loading: generatingProposals }] = useGenerateProposalsMutation();

  const [approveProposal, { loading: approvingProposal }] = useApproveProposalMutation();

  const pending = proposalsData?.pendingProposals;
  const coverage = gapsData?.regulatoryCoverageGaps;

  const handleGenerateProposals = async () => {
    if (!coverage) return;
    const result = await generateProposals({
      variables: {
        input: {
          rulesetId: rulesetId ?? '',
        },
      },
    });

    if (result.data?.generateConfigProposals.success) {
      refetchProposals();
    }
  };

  const handleApprove = async (proposalId: string) => {
    const result = await approveProposal({
      variables: {
        input: {
          proposalId,
          approvedBy: 'architect',
        },
      },
    });

    if (result.data?.approveConfigProposal.success) {
      setActionedIds((prev) => ({ ...prev, [proposalId]: 'approved' }));
    }
  };

  const handleReject = (proposalId: string) => {
    setActionedIds((prev) => ({ ...prev, [proposalId]: 'rejected' }));
  };

  const handleOpenProposal = (proposal: PendingProposal) => {
    setSelectedProposal(proposal);
    openModal();
  };

  const activeProposals = pending?.proposals.filter((p) => !actionedIds[p.proposalId]) ?? [];
  const approvedCount = Object.values(actionedIds).filter((s) => s === 'approved').length;
  const rejectedCount = Object.values(actionedIds).filter((s) => s === 'rejected').length;

  return (
    <Container size="xl" py="xl">
      <Group justify="space-between" mb="lg">
        <div>
          <Title order={2}>Proposal Review</Title>
          <Text size="sm" c="dimmed">
            Review coverage gaps and approve/reject config proposals
          </Text>
        </div>
        <Badge variant="light" color="violet" size="lg">Architect</Badge>
      </Group>

      {proposalsError && (
        <Alert icon={<IconAlertTriangle size={16} />} color="red" title="Error Loading Proposals" mb="lg">
          {proposalsError.message}
        </Alert>
      )}

      {gapsError && (
        <Alert icon={<IconAlertTriangle size={16} />} color="red" title="Error Loading Gaps" mb="lg">
          {gapsError.message}
        </Alert>
      )}

      {/* Stats cards for pending proposals */}
      {pending && pending.totalCount > 0 && <ProposalStatsCards data={pending} />}

      <Tabs defaultValue="proposals">
        <Tabs.List mb="md">
          <Tabs.Tab value="proposals" leftSection={<IconSparkles size={16} />}>
            Pending Proposals ({activeProposals.length})
          </Tabs.Tab>
          <Tabs.Tab value="gaps" leftSection={<IconShieldX size={16} />}>
            Gap Analysis
          </Tabs.Tab>
        </Tabs.List>

        {/* Proposals Tab */}
        <Tabs.Panel value="proposals">
          {proposalsLoading && (
            <Stack align="center" py="xl">
              <Loader size="lg" />
              <Text c="dimmed">Loading pending proposals...</Text>
            </Stack>
          )}

          {(approvedCount > 0 || rejectedCount > 0) && (
            <Alert color="green" variant="light" mb="md" icon={<IconCheck size={16} />}>
              {approvedCount > 0 && `${approvedCount} approved`}
              {approvedCount > 0 && rejectedCount > 0 && ', '}
              {rejectedCount > 0 && `${rejectedCount} rejected`}
              {' this session.'}
            </Alert>
          )}

          {!proposalsLoading && activeProposals.length === 0 && (
            <Alert color="blue" variant="light">
              No pending proposals. Use the Gap Analysis tab to find gaps and generate new proposals.
            </Alert>
          )}

          <Stack gap="sm">
            {activeProposals.map((proposal) => (
              <Card
                key={proposal.proposalId}
                withBorder
                p="sm"
                style={{ cursor: 'pointer' }}
                onClick={() => handleOpenProposal(proposal)}
              >
                <Group justify="space-between">
                  <Group gap="xs">
                    <Code fw={700}>{proposal.proposalId}</Code>
                    <Badge
                      variant="light"
                      size="sm"
                      color={CONFIG_TYPE_COLORS[proposal.configType] ?? 'gray'}
                    >
                      {CONFIG_TYPE_LABELS[proposal.configType] ?? proposal.configType}
                    </Badge>
                    {proposal.safetyLevel && (
                      <Badge
                        variant="light"
                        size="sm"
                        color={SAFETY_COLORS[proposal.safetyLevel] ?? 'gray'}
                      >
                        {proposal.safetyLevel}
                      </Badge>
                    )}
                    {proposal.statuteRef && (
                      <Text size="xs" c="dimmed">{proposal.statuteRef}</Text>
                    )}
                  </Group>
                  <Group gap="xs">
                    {proposal.provisions.length > 0 && (
                      <Badge size="xs" variant="light">{proposal.provisions.length} provisions</Badge>
                    )}
                    {proposal.states.length > 0 && (
                      <Badge size="xs" variant="light">{proposal.states.length} states</Badge>
                    )}
                    <IconEye size={16} color="var(--mantine-color-dimmed)" />
                  </Group>
                </Group>
                {proposal.displayName && (
                  <Text size="sm" fw={500} mt={4}>{proposal.displayName}</Text>
                )}
                {proposal.description && (
                  <Text size="xs" c="dimmed" lineClamp={1} mt={2}>{proposal.description}</Text>
                )}
              </Card>
            ))}
          </Stack>
        </Tabs.Panel>

        {/* Gap Analysis Tab */}
        <Tabs.Panel value="gaps">
          <Card withBorder p="md" mb="lg">
            <Group>
              <Select
                placeholder="Select a ruleset to analyze..."
                leftSection={<IconSearch size={16} />}
                data={rulesetOptions}
                value={rulesetId}
                onChange={setRulesetId}
                searchable
                clearable
                nothingFoundMessage="No rulesets found"
                style={{ flex: 1 }}
              />
            </Group>
          </Card>

          {gapsLoading && (
            <Stack align="center" py="xl">
              <Loader size="lg" />
              <Text c="dimmed">Analyzing coverage gaps...</Text>
            </Stack>
          )}

          {coverage && (
            <>
              <CoverageStatsCards data={coverage} />

              {coverage.gaps.length === 0 ? (
                <Alert color="green" icon={<IconShieldCheck size={16} />}>
                  Full coverage — no gaps detected for {coverage.rulesetId}.
                </Alert>
              ) : (
                <>
                  <Group justify="flex-end" mb="md">
                    <Button
                      leftSection={<IconSparkles size={16} />}
                      onClick={handleGenerateProposals}
                      loading={generatingProposals}
                    >
                      Generate Proposals for All Gaps
                    </Button>
                  </Group>

                  <Table striped highlightOnHover>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Rule ID</Table.Th>
                        <Table.Th>Rule Name</Table.Th>
                        <Table.Th>Missing Config Type</Table.Th>
                        <Table.Th>Description</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {coverage.gaps.flatMap((gap) =>
                        gap.missingConfigs.map((mc, j) => (
                          <Table.Tr key={`${gap.ruleId}-${j}`}>
                            <Table.Td><Code>{gap.ruleId}</Code></Table.Td>
                            <Table.Td><Text size="sm">{gap.ruleName}</Text></Table.Td>
                            <Table.Td><Badge variant="light" size="sm">{mc.configType}</Badge></Table.Td>
                            <Table.Td><Text size="sm">{mc.description}</Text></Table.Td>
                          </Table.Tr>
                        )),
                      )}
                    </Table.Tbody>
                  </Table>
                </>
              )}
            </>
          )}
        </Tabs.Panel>
      </Tabs>

      {/* Proposal Detail Modal */}
      <ProposalDetailModal
        proposal={selectedProposal}
        opened={modalOpened}
        onClose={closeModal}
        onApprove={handleApprove}
        onReject={handleReject}
        approving={approvingProposal}
      />
    </Container>
  );
}
